import { StatusBadge, type BadgeTone } from './ui/StatusBadge';
import { statusCounts, type CampaignGroup } from '../lib/campaignGroups';
import type { Campaign, CampaignStatus } from '../types/api';

/** Exported so anywhere else that shows a campaign's status (e.g. the calendar) uses the same tone per status, never a second, drifting color mapping. */
export const CAMPAIGN_STATUS_CONFIG: Record<
  CampaignStatus,
  { tone: BadgeTone; icon: string; label: string }
> = {
  draft: { tone: 'neutral', icon: '○', label: 'Draft' },
  scheduled: { tone: 'accent', icon: '◷', label: 'Scheduled' },
  sending: { tone: 'success', icon: '●', label: 'Sending' },
  paused: { tone: 'warning', icon: '❙❙', label: 'Paused' },
  sent: { tone: 'success', icon: '✓', label: 'Sent' },
  failed: { tone: 'danger', icon: '✗', label: 'Failed' },
};

/** A status this build does not know yet must never crash the page. */
const UNKNOWN_STATUS = { tone: 'neutral', icon: '?', label: 'Unknown' } as const;

export function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  const c = CAMPAIGN_STATUS_CONFIG[status] ?? UNKNOWN_STATUS;
  return (
    <StatusBadge tone={c.tone} icon={c.icon}>
      {c.label}
    </StatusBadge>
  );
}

/** The language versions of one send: their status, or e.g. "2 sent · 1 draft" when they differ. */
export function CampaignGroupStatusBadge({ group }: { group: CampaignGroup }) {
  const counts = statusCounts(group);
  const only = counts.length === 1 ? counts[0] : undefined;
  if (only) return <CampaignStatusBadge status={only.status} />;
  return (
    <StatusBadge tone="neutral" icon="◐">
      {counts
        .map(
          (x) => `${x.count} ${CAMPAIGN_STATUS_CONFIG[x.status]?.label.toLowerCase() ?? x.status}`,
        )
        .join(' · ')}
    </StatusBadge>
  );
}

/**
 * "Pending approval" is not a status in the API: it is a draft whose test
 * email has been sent, so the next step is an admin's approval.
 */
export function isAwaitingApproval(c: Pick<Campaign, 'status' | 'testSendDoneAt'>): boolean {
  return c.status === 'draft' && c.testSendDoneAt !== null;
}

export function PendingApprovalBadge() {
  return (
    <StatusBadge tone="warning" icon="◐">
      Pending approval
    </StatusBadge>
  );
}
